import { React, useState, useEffect, useContext } from "react";
import "../../../assets/css/table.css";
import Loading from "react-fullscreen-loading";
import { Link } from "react-router-dom";
import HeaderSection from "../banner/HeaderSection";
import DivLoader from "../../../component/utilities/Spinner";
import DataTable from "react-data-table-component";
import axios from "axios";
import { toast } from "react-toastify";
import * as CONST from "../../../component/config/Constant";
import Form from "../../../component/utilities/Forms";
import { Modal, Button, Row, Col } from "react-bootstrap";
import { AuthContext } from "../../../component/context/AuthContext";
import Moment from "moment";
import Swal from "sweetalert2";
import { EditIcon, DeleteIcon, ViewIcon } from "../../../component/utilities/ActionIcons";

function RequestSubscription() {
  const [authUserData, setAuthUserData] = useContext(AuthContext);
  const [fullLoading, setFullLoading] = useState(false);
  const [validate, setValidate] = useState({});
  const [mySubscriptions, setMySubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [show, setShow] = useState(false);
  const [subscriptionTypes, setSubscriptionTypes] = useState([]);
  const [disabled, setDisabled] = useState(false);

  // View mode states
  const [viewShow, setViewShow] = useState(false);
  const [viewData, setViewData] = useState(null);

  // Edit mode state
  const [editingId, setEditingId] = useState(null); // null = add mode, id = edit mode

  // Form fields
  const [subscriptionTypeId, setSubscriptionTypeId] = useState("");
  const [cost, setCost] = useState("");
  const [currency, setCurrency] = useState("INR");
  const [duration, setDuration] = useState("");
  const [durationUnit, setDurationUnit] = useState("day");
  const [description, setDescription] = useState("");

  // Search states
  const [srchSubName, setSrchSubName] = useState("");
  const [srchStatus, setSrchStatus] = useState("");
  const [searchParam, setSearchParam] = useState({
    name: "",
    status: ""
  });

  /* DATATABLE state */
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [maxPageLimit, setMaxPageLimit] = useState(10);
  const [minPageLimit, setMinPageLimit] = useState(0);
  const pageNumberLimit = 10;

  let isToken = "";
  if (authUserData && authUserData[0]) {
    isToken = authUserData[0].token || "";
  }

  useEffect(() => {
    fetchSubscriptionTypes();
    fetchMySubscriptions(activePage, perPage, searchParam);
  }, [activePage, searchParam]);

  async function fetchSubscriptionTypes() {
    await axios.get(CONST.GET_SUBSCRIPTION_TYPE_LIST, {
      headers: { Authorization: `Bearer ${isToken}` }
    })
    .then(res => {
      if (res.data.status === "success") {
        setSubscriptionTypes(res.data.data.filter(item => item.is_active == "1"));
      }
    })
    .catch(err => console.log(err));
  }

  async function fetchMySubscriptions(page, limit, search = null) {
    setLoading(true);
    let params = {
      page_number: page,
      limit: limit,
      subscription_type_name: search?.name || "",
      status: search?.status || ""
    };

    await axios.get(CONST.GET_MY_SUBSCRIPTIONS, {
      params: params,
      headers: { Authorization: `Bearer ${isToken}` }
    })
    .then(res => {
      if (res.data.status === "success") {
        setMySubscriptions(res.data.data);
        setTotalRows(res.data.count || res.data.data.length);
      } else {
        setMySubscriptions([]);
      }
    })
    .catch(err => setMySubscriptions([]))
    .finally(() => setLoading(false));
  }

  // Open modal for adding new request
  const handleShow = () => {
    setEditingId(null);
    setSubscriptionTypeId("");
    setCost("");
    setCurrency("INR");
    setDuration("");
    setDurationUnit("day");
    setDescription("");
    setValidate({});
    setShow(true);
  };

  // Open modal pre-filled for editing a pending request
  const handleEdit = (row) => {
    setEditingId(row.id);
    setSubscriptionTypeId(row.subscription_type_id || "");
    setCost(row.cost || "");
    setCurrency(row.currency || "INR");
    setDuration(row.duration || "");
    setDurationUnit(row.duration_unit || "day");
    setDescription(row.description || "");
    setValidate({});
    setShow(true);
  };

  const handleClose = () => {
    setShow(false);
    setEditingId(null);
  };

  // View handlers
  const handleViewShow = (row) => {
    setViewData(row);
    setViewShow(true);
  };

  const handleViewClose = () => {
    setViewShow(false);
    setViewData(null);
  };

  // Delete a pending request
  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to delete this subscription request?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setFullLoading(true);
        await axios.post(CONST.DELETE_SUBSCRIPTION_REQUEST, { id }, {
          headers: { Authorization: `Bearer ${isToken}` }
        })
        .then(res => {
          if (res.data.status === "success") {
            toast.success("Subscription request deleted successfully");
            fetchMySubscriptions(activePage, perPage, searchParam);
          } else {
            toast.error(res.data.message || "Something went wrong");
          }
        })
        .catch(() => toast.error("Something went wrong"))
        .finally(() => setFullLoading(false));
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let validator = Form.validator({
      subscriptionTypeId: { value: subscriptionTypeId, isRequired: true, label: "Subscription Type" },
      cost: { value: cost, isRequired: true, label: "Cost" },
      currency: { value: currency, isRequired: true, label: "Currency" },
      duration: { value: duration, isRequired: true, label: "Duration" },
      durationUnit: { value: durationUnit, isRequired: true, label: "Duration Unit" },
    });

    if (validator !== null) {
      setValidate({ validate: validator.errors });
      return;
    }

    let customErrors = {};
    if (parseFloat(cost) < 0) {
      customErrors.cost = ["Cost cannot be negative"];
    }
    if (parseFloat(duration) < 1) {
      customErrors.duration = ["Duration must be at least 1"];
    }

    if (Object.keys(customErrors).length > 0) {
      setValidate({ validate: customErrors });
      return;
    }

    setDisabled(true);
    setFullLoading(true);

    const payload = {
      subscription_type_id: subscriptionTypeId,
      cost: cost,
      currency: currency,
      duration: duration,
      duration_unit: durationUnit,
      description: description
    };

    // If editing, add id and call update endpoint; otherwise call apply
    const apiUrl = editingId ? CONST.EDIT_SUBSCRIPTION_REQUEST : CONST.APPLY_SUBSCRIPTION;
    if (editingId) payload.id = editingId;

    await axios.post(apiUrl, payload, {
      headers: { Authorization: `Bearer ${isToken}` }
    })
    .then(res => {
      if (res.data.status === "success") {
        toast.success(editingId ? "Subscription request updated successfully" : "Subscription request submitted successfully");
        fetchMySubscriptions(activePage, perPage, searchParam);
        handleClose();
      } else {
        const msg = res.data.message || "Something went wrong";
        if (msg.includes('"')) {
          const match = msg.match(/"([^"]+)"/);
          if (match) {
            const backendField = match[1];
            const fieldMapping = {
              'subscription_type_id': 'subscriptionTypeId',
              'duration_unit': 'durationUnit'
            };
            const frontendField = fieldMapping[backendField] || backendField;
            setValidate({ validate: { [frontendField]: [msg] } });
            return;
          }
        }
        toast.error(msg);
      }
    })
    .catch(() => toast.error("Something went wrong"))
    .finally(() => { setFullLoading(false); setDisabled(false); });
  };

  const handleSearch = () => {
    setSearchParam({
      name: srchSubName,
      status: srchStatus
    });
    setActivePage(1);
  };

  const handleClear = () => {
    setSrchSubName("");
    setSrchStatus("");
    setSearchParam({
      name: "",
      status: ""
    });
    setActivePage(1);
  };

  const columns = [
    {
      name: "Sr. No",
      selector: (row, index) => (activePage - 1) * perPage + index + 1,
      width: "8%",
    },
    {
      name: "Subscription",
      selector: (row) => row.subscription_name || row.subscriptionType?.name || "N/A",
      width: "15%",
    },
    {
      name: "Date",
      selector: (row) => Moment(row.created_at).format("DD MMM YYYY"),
      width: "12%",
    },
    {
      name: "Cost",
      selector: (row) => `${row.cost} ${row.currency}`,
      width: "12%",
    },
    {
      name: "Duration",
      selector: (row) => `${row.duration} ${row.duration_unit}(s)`,
      width: "12%",
    },
    {
      name: "Status",
      cell: (row) => (
        <span className={`db-status-badge ${
          row.status === "approved" || row.status === "active" ? "db-badge-success" : 
          row.status === "rejected" || row.status === "cancelled" ? "db-badge-danger" : 
          row.status === "expired" ? "db-badge-secondary" : "db-badge-warning"
        }`}>
          {(row.status || "pending").charAt(0).toUpperCase() + (row.status || "pending").slice(1)}
        </span>
      ),
      width: "10%",
    },
    {
      name: "Action",
      width: "15%",
      cell: (row) => {
        const isPending = !row.status || row.status.toLowerCase() === "pending";
        return (
          <div className="d-flex align-items-center gap-2">
            <ViewIcon onClick={() => handleViewShow(row)} tooltip="View" />
            {isPending && (
              <>
                <EditIcon onClick={() => handleEdit(row)} tooltip="Edit" />
                <DeleteIcon onClick={() => handleDelete(row.id)} tooltip="Delete" />
              </>
            )}
          </div>
        );
      },
    },
  ];

  const BootyPagination = ({ rowCount, currentPage, onChangePage }) => {
    const pages = Math.ceil(rowCount / perPage);
    const pageNumbers = [];
    for (let i = 1; i <= pages; i++) {
        if (i <= maxPageLimit && i > minPageLimit) pageNumbers.push(i);
    }

    return (
      <div className="card-footer d-flex align-items-center">
        <p className="m-0 text-muted">Showing {(currentPage-1)*perPage+1} to {Math.min(currentPage*perPage, rowCount)} of {rowCount} entries</p>
        <ul className="pagination m-0 ms-auto">
          <li className="page-item"><button className="page-link" disabled={currentPage === 1} onClick={() => onChangePage(currentPage - 1)}>«</button></li>
          {pageNumbers.map(page => (
            <li key={page} className={`page-item ${currentPage === page ? "active" : ""}`}>
              <button className="page-link" onClick={() => onChangePage(page)}>{page}</button>
            </li>
          ))}
          <li className="page-item"><button className="page-link" disabled={currentPage === pages} onClick={() => onChangePage(currentPage + 1)}>»</button></li>
        </ul>
      </div>
    );
  };

  return (
    <>
      <Loading loading={fullLoading} background="rgba(0, 0, 0, 0.5)" loaderColor="#fff" />
      <HeaderSection
        title="My Subscriptions"
        heading="Request Subscription"
        addButton={handleShow}
        addLink="#"
        addLinkName="New Request"
        linktype="add"
      />
      <div className="page-body">
        <div className="container-xl">
          <div className="card">
            <div className="card-header"><h3 className="card-title">My Requests</h3></div>
            <div className="card-body border-bottom py-3">
              <div className="row g-2">
                <div className="col-md-3">
                  <div className="form-group mb-0">
                    <label className="form-label">Subscription</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search subscription"
                      onChange={(e) => setSrchSubName(e.target.value)}
                      value={srchSubName}
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-group mb-0">
                    <label className="form-label">Status</label>
                    <select
                      className="form-select form-control"
                      onChange={(e) => setSrchStatus(e.target.value)}
                      value={srchStatus}
                    >
                      <option value="">All Statuses</option>
                      <option value="pending">Pending</option>
                      <option value="active">Active</option>
                      <option value="rejected">Rejected</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="expired">Expired</option>
                    </select>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-group mb-0">
                    <label className="form-label">&nbsp;</label>
                    <div className="d-flex gap-2">
                      <button className="btn btn-blue" onClick={handleSearch}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-search" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                          <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                          <circle cx="10" cy="10" r="7"></circle>
                          <line x1="21" y1="21" x2="15" y2="15"></line>
                        </svg>
                        Search
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm ml-2"
                        onClick={handleClear}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-x" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                          <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                        Clear
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="table-responsive">
              <DataTable
                columns={columns}
                data={mySubscriptions}
                progressPending={loading}
                progressComponent={<div style={{padding: '24px'}}><DivLoader color="primary" /></div>}
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                onChangePage={setActivePage}
                paginationPerPage={perPage}
                paginationComponent={BootyPagination}
              />
            </div>
          </div>
        </div>
      </div>

      <Modal show={show} onHide={handleClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingId ? "Edit Subscription Request" : "New Subscription Request"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="form-label required">Subscription Type</label>
              <select
                className={`form-select ${validate.validate && validate.validate.subscriptionTypeId ? "is-invalid" : ""}`}
                onChange={(e) => setSubscriptionTypeId(e.target.value)}
                value={subscriptionTypeId}
              >
                <option value="">Select Type</option>
                {subscriptionTypes.map(type => <option key={type.id} value={type.id}>{type.name}</option>)}
              </select>
              {validate.validate && validate.validate.subscriptionTypeId && <div className="invalid-feedback">{validate.validate.subscriptionTypeId}</div>}
            </div>

            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <label className="form-label required">Cost</label>
                  <input
                    type="number"
                    step="0.01"
                    className={`form-control ${validate.validate && validate.validate.cost ? "is-invalid" : ""}`}
                    min="0"
                    placeholder="0.00"
                    onChange={(e) => setCost(e.target.value)}
                    value={cost}
                  />
                  {validate.validate && validate.validate.cost && <div className="invalid-feedback">{validate.validate.cost}</div>}
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <label className="form-label required">Currency</label>
                  <select
                    className={`form-select ${validate.validate && validate.validate.currency ? "is-invalid" : ""}`}
                    onChange={(e) => setCurrency(e.target.value)}
                    value={currency}
                  >
                    <option value="INR">INR</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                  {validate.validate && validate.validate.currency && <div className="invalid-feedback">{validate.validate.currency}</div>}
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <label className="form-label required">Duration</label>
                  <input
                    type="number"
                    min="1"
                    className={`form-control ${validate.validate && validate.validate.duration ? "is-invalid" : ""}`}
                    placeholder="e.g. 1, 12"
                    onChange={(e) => setDuration(e.target.value)}
                    value={duration}
                  />
                  {validate.validate && validate.validate.duration && <div className="invalid-feedback">{validate.validate.duration}</div>}
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <label className="form-label required">Unit</label>
                  <select
                    className={`form-select ${validate.validate && validate.validate.durationUnit ? "is-invalid" : ""}`}
                    onChange={(e) => setDurationUnit(e.target.value)}
                    value={durationUnit}
                  >
                    <option value="day">Day(s)</option>
                    <option value="month">Month(s)</option>
                    <option value="year">Year(s)</option>
                  </select>
                  {validate.validate && validate.validate.durationUnit && <div className="invalid-feedback">{validate.validate.durationUnit}</div>}
                </div>
              </Col>
            </Row>

            <div className="mb-3">
              <label className="form-label">Description / Justification</label>
              <textarea
                className="form-control"
                rows="3"
                placeholder="Why do you need this?"
                onChange={(e) => setDescription(e.target.value)}
                value={description}
              ></textarea>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>Close</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={disabled}>
            {disabled ? "Submitting..." : (editingId ? "Update Request" : "Submit Request")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* View Modal */}
      <Modal show={viewShow} onHide={handleViewClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>View Subscription Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewData && (
            <div>
              <div className="row">
                <div className="col-md-6">
                  <p className="mb-1"><strong>Subscription:</strong> <span className="text-muted">{viewData.subscription_name || viewData.subscriptionType?.name || "N/A"}</span></p>
                  <p className="mb-1"><strong>Cost:</strong> <span className="text-muted">{viewData.cost} {viewData.currency}</span></p>
                </div>
                <div className="col-md-6">
                  <p className="mb-1"><strong>Duration:</strong> <span className="text-muted">{viewData.duration} {viewData.duration_unit}(s)</span></p>
                  <p className="mb-1"><strong>Requested Date:</strong> <span className="text-muted">{Moment(viewData.created_at).format("DD MMM YYYY")}</span></p>
                </div>
              </div>
              <div className="row mt-2">
                <div className="col-md-6">
                  <p className="mb-1"><strong>Status:</strong> <span className="text-muted">{(viewData.status || "pending").charAt(0).toUpperCase() + (viewData.status || "pending").slice(1)}</span></p>
                </div>
              </div>
              <hr className="my-2" />
              <p className="mb-1 font-weight-bold"><strong>Description / Justification:</strong></p>
              <p className="text-muted small">{viewData.description || "N/A"}</p>
              
              {(viewData.status === "active" || viewData.status === "approved") && (
                <>
                  <hr className="my-2" />
                  <div className="row">
                    <div className="col-md-6">
                      <p className="mb-1"><strong>Start Date:</strong> <span className="text-muted">{viewData.start_date ? Moment(viewData.start_date).format("DD MMM YYYY") : "N/A"}</span></p>
                    </div>
                    <div className="col-md-6">
                      <p className="mb-1"><strong>End Date:</strong> <span className="text-muted">{viewData.end_date ? Moment(viewData.end_date).format("DD MMM YYYY") : "N/A"}</span></p>
                    </div>
                  </div>
                </>
              )}
              {viewData.status === "rejected" && viewData.rejection_reason && (
                <>
                  <hr className="my-2" />
                  <p className="mb-1 font-weight-bold"><strong>Rejection Reason:</strong></p>
                  <p className="text-muted small">{viewData.rejection_reason}</p>
                </>
              )}
              {viewData.status === "cancelled" && viewData.rejection_reason && (
                <>
                  <hr className="my-2" />
                  <p className="mb-1 font-weight-bold"><strong>Cancellation Reason:</strong></p>
                  <p className="text-muted small">{viewData.rejection_reason}</p>
                </>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleViewClose}>Close</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default RequestSubscription;
